import * as React from 'react';

import Auth from '../../../../stores/authentication';
import Authorization from '../../../../stores/Authorization';
import { IOrganizationRoleModel } from '../../../../interfaces/IUser';
import { LocalRoutes } from '../../../../utilities/LocalRoutes';
import { MenuItemNavLinkWithIcon } from '../../MenuItemNavLinkWithIcon';
import { RolesEnum } from '../../../../utilities/Constants';
import { Stack } from 'react-bootstrap';
import { ZoneSwitchMenuItem } from './ZoneSwitchMenuItem';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useCaseFileModelContext } from '../../../../contexts/useCaseFileModelContext';
import { useZoneContext } from '../../../../contexts/useZoneContext';

export interface IUserMenuProps {
    hideSelectedZoneName?: boolean;
    onZoneChange?: (org: IOrganizationRoleModel, redirectPath?: string) => void;
}

export function UserMenu(props: IUserMenuProps) {
    {
        const { user, isLoggedIn, showLogout } = useAuthContext();
        const zoneContext = useZoneContext();
        const { caseFileModelIdChange } = useCaseFileModelContext();

        const isPlanningExecutionEnabled = () => {
            if (user && user.organizationRoles) {
                for (let i = 0; i < user.organizationRoles.length; i++) {
                    if (user.organizationRoles[i].default) {
                        const defaultOrganization = user.organizationRoles[i];
                        return defaultOrganization.enablePlanningExecution;
                    }
                }
            }
            return false;
        };

        const isSystemAdmin = user ? Auth.isSystemAdmin(user) : false;
        const showManageSameCasesMenuItem = Auth.isInApplicationRole(RolesEnum.SameCaseAdmin, user);
        const isQuadRoute = window.location.pathname.startsWith('/quad');

        const showTriggerRulesMenuItem = Authorization.isAuthorizedToRoute(
            LocalRoutes.Triggers,
            user,
            zoneContext.zoneGuid
        );

        const handleAfterLogout = () => {
            caseFileModelIdChange(0);
            zoneContext.setupZone('', '');
        };

        if (!user.profile) {
            return null;
        }

        return (
            <div
                style={{
                    display: 'flex',
                    gap: '7vw',
                }}
            >
                {props.onZoneChange && (
                    <ZoneSwitchMenuItem
                        onZoneChange={props.onZoneChange}
                        hideSelectedZoneName={props.hideSelectedZoneName}
                    />
                )}

                {isLoggedIn && (
                    <div className="innerContainer" style={{ paddingRight: '2.5vw' }}>
                        <button className="btn-no-bg" data-bs-toggle="dropdown">
                            <Stack
                                direction="horizontal"
                                gap={2}
                                style={{ whiteSpace: 'nowrap', color: '#ffffff' }}
                            >
                                <i className="fal fa-user" />
                                <span>
                                    {user?.profile?.firstName}&nbsp;
                                    {user?.profile?.lastName}
                                </span>
                            </Stack>
                        </button>
                        <div className="dropdown-menu adjust-right">
                            <div>
                                {showManageSameCasesMenuItem && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-game-board"
                                        label="Manage Same Case Groups"
                                        to={LocalRoutes.QuadAdminSameCaseManage}
                                    />
                                )}
                                {!isSystemAdmin && !isQuadRoute && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-cog"
                                        label="User Settings"
                                        to={LocalRoutes.UserSettings}
                                    />
                                )}
                                {!isSystemAdmin && isQuadRoute && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-cog"
                                        label="User Settings"
                                        to={LocalRoutes.QuadUserSettings}
                                    />
                                )}
                                <MenuItemNavLinkWithIcon
                                    icon="fal fa-bullhorn"
                                    label="Announcements"
                                    to={LocalRoutes.Announcements}
                                />
                                {Authorization.isAuthorizedToRoute(
                                    LocalRoutes.Zones,
                                    user,
                                    zoneContext.zoneGuid
                                ) && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-building"
                                        label="Zones"
                                        to={LocalRoutes.Zones}
                                    />
                                )}
                                {Authorization.isAuthorizedToRoute(
                                    LocalRoutes.AssertionDescriptors,
                                    user,
                                    zoneContext.zoneGuid
                                ) && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-list"
                                        label="Assertion Descriptors"
                                        to={LocalRoutes.AssertionDescriptors}
                                    />
                                )}
                                {Authorization.isAuthorizedToRoute(
                                    LocalRoutes.Users,
                                    user,
                                    zoneContext.zoneGuid
                                ) && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-users"
                                        label="Users"
                                        to={LocalRoutes.Users}
                                    />
                                )}
                                {isPlanningExecutionEnabled() &&
                                    Authorization.isAuthorizedToRoute(
                                        LocalRoutes.UserZoneProfile,
                                        user,
                                        zoneContext.zoneGuid
                                    ) && (
                                        <MenuItemNavLinkWithIcon
                                            icon="fal fa-user-plus"
                                            label="User Zone Profile"
                                            to={LocalRoutes.UserZoneProfile}
                                        />
                                    )}
                                {Authorization.isAuthorizedToRoute(
                                    LocalRoutes.CreateInvitation,
                                    user,
                                    zoneContext.zoneGuid
                                ) && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-user-plus"
                                        label="User Invitation"
                                        to={LocalRoutes.CreateInvitation}
                                    />
                                )}
                                {isPlanningExecutionEnabled() &&
                                    Authorization.isAuthorizedToRoute(
                                        LocalRoutes.UserTasks,
                                        user,
                                        zoneContext.zoneGuid
                                    ) && (
                                        <MenuItemNavLinkWithIcon
                                            icon="fal fa-tasks"
                                            label="My Task List"
                                            to={LocalRoutes.UserTasks}
                                        />
                                    )}
                                {isPlanningExecutionEnabled() &&
                                    Authorization.isAuthorizedToRoute(
                                        LocalRoutes.TaskBarSetting,
                                        user,
                                        zoneContext.zoneGuid
                                    ) && (
                                        <MenuItemNavLinkWithIcon
                                            icon="fal fa-cog"
                                            label="Task Bar Settings"
                                            to={LocalRoutes.TaskBarSetting}
                                        />
                                    )}
                                {showTriggerRulesMenuItem && !isQuadRoute && (
                                    <MenuItemNavLinkWithIcon
                                        icon="fal fa-chevrons-right"
                                        label="Trigger Rules"
                                        to={`${zoneContext.buildZoneUrl(LocalRoutes.Triggers)}`}
                                    />
                                )}
                                <MenuItemNavLinkWithIcon
                                    icon="fal fa-graduation-cap"
                                    label="Tutorials"
                                    to={LocalRoutes.ViewSpecificPDF.replace(':id', '1')}
                                />
                                <MenuItemNavLinkWithIcon
                                    icon="fal fa-sign-out-alt"
                                    label="Logout"
                                    onClick={() => showLogout(() => handleAfterLogout())}
                                    to={LocalRoutes.Logout}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
